const CONSENT_COOKIE_NAME = 'cc_cookie_byscuit';
const REQUIRED_CATEGORY = 'functional';
const IFRAME_SELECTOR = 'iframe.js-consent-iframe[data-src]';
const SCRIPT_SELECTOR = 'script.js-consent-script[data-src]';
const EVENT_NAMES = [
  'byscuit:consent:changed',
  'cc:onChange',
  'cc:onAccept',
  'cc:onConsent',
];

const CATEGORY_ALIASES = {
  functional: ['functional', 'fonctionnel', 'fonctionnelle', 'fonctional'],
};

function getCookieValue(name) {
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : '';
}

function parseConsentCookie() {
  const raw = getCookieValue(CONSENT_COOKIE_NAME);

  if (!raw) {
    return null;
  }

  const candidates = [raw];
  if (raw.startsWith('"') && raw.endsWith('"')) {
    candidates.push(raw.slice(1, -1).replace(/\\"/g, '"'));
  }

  try {
    return JSON.parse(candidates[0]);
  } catch (e1) {
    try {
      return JSON.parse(candidates[1] || '');
    } catch (e2) {
      return null;
    }
  }
}

function normalizeCategory(value) {
  return String(value || '').trim().toLowerCase();
}

function categoryAliases(category) {
  const normalized = normalizeCategory(category);
  return CATEGORY_ALIASES[normalized] || [normalized];
}

function isCategoryAcceptedFromCookie(category, consent) {
  if (!consent) {
    return false;
  }

  // Some CMP payloads use "all" flags instead of explicit categories.
  if (
    consent.accept_type === 'all' ||
    consent.accepted_all === true ||
    consent.level === 'all' ||
    consent.all === true
  ) {
    return true;
  }

  const accepted = Array.isArray(consent.categories)
    ? consent.categories
    : (Array.isArray(consent.acceptedCategories)
      ? consent.acceptedCategories
      : (Array.isArray(consent.level) ? consent.level : []));

  if (!accepted.length) {
    return false;
  }

  const aliases = categoryAliases(category);
  return accepted.some((entry) => aliases.indexOf(normalizeCategory(entry)) !== -1);
}

function isCategoryAcceptedFromApi(category) {
  const aliases = categoryAliases(category);
  const apiCandidates = [window.CC, window.cc, window.CookieConsent];

  for (let i = 0; i < apiCandidates.length; i += 1) {
    const api = apiCandidates[i];
    if (!api || typeof api.allowedCategory !== 'function') {
      continue;
    }

    for (let j = 0; j < aliases.length; j += 1) {
      try {
        if (api.allowedCategory(aliases[j])) {
          return true;
        }
      } catch (e) {
        // continue
      }
    }
  }

  return false;
}

function getConsentState() {
  return {
    cookieConsent: parseConsentCookie(),
  };
}

function isConsentGranted(category = REQUIRED_CATEGORY, consentState = null) {
  const state = consentState || getConsentState();

  if (state.cookieConsent) {
    return isCategoryAcceptedFromCookie(category, state.cookieConsent);
  }

  try {
    return isCategoryAcceptedFromApi(category);
  } catch (e) {
    return false;
  }
}

function activateDeferredIframes() {
  const iframes = Array.from(document.querySelectorAll(IFRAME_SELECTOR));
  if (!iframes.length) {
    return 0;
  }

  const consentState = getConsentState();
  let remaining = iframes.length;

  iframes.forEach((iframe) => {
    const requiredCategory = iframe.getAttribute('data-requires-consent') || REQUIRED_CATEGORY;
    if (!isConsentGranted(requiredCategory, consentState)) {
      return;
    }

    const src = iframe.getAttribute('data-src');
    if (!src || iframe.getAttribute('src')) {
      return;
    }

    iframe.setAttribute('src', src);
    iframe.setAttribute('data-consent-loaded', '1');
    iframe.removeAttribute('data-src');
    remaining -= 1;
  });

  return remaining;
}

function copyScriptAttributes(fromNode, toNode) {
  const attrs = ['async', 'defer', 'crossorigin', 'referrerpolicy', 'integrity', 'nonce'];

  attrs.forEach((attr) => {
    if (!fromNode.hasAttribute(attr)) {
      return;
    }

    const value = fromNode.getAttribute(attr);
    if (value === null || value === '') {
      toNode.setAttribute(attr, '');
      return;
    }

    toNode.setAttribute(attr, value);
  });
}

function activateDeferredScripts() {
  const scriptNodes = Array.from(document.querySelectorAll(SCRIPT_SELECTOR));
  if (!scriptNodes.length) {
    return 0;
  }

  const consentState = getConsentState();
  let remaining = scriptNodes.length;

  scriptNodes.forEach((scriptNode) => {
    const requiredCategory = scriptNode.getAttribute('data-requires-consent') || REQUIRED_CATEGORY;
    if (!isConsentGranted(requiredCategory, consentState)) {
      return;
    }

    const src = scriptNode.getAttribute('data-src');
    if (!src) {
      return;
    }

    const loaded = scriptNode.getAttribute('data-consent-loaded') === '1';
    if (loaded) {
      remaining -= 1;
      return;
    }

    const injected = document.createElement('script');
    const originalType = scriptNode.getAttribute('data-script-type');
    if (originalType) {
      injected.setAttribute('type', originalType);
    }

    copyScriptAttributes(scriptNode, injected);
    injected.setAttribute('src', src);
    injected.setAttribute('data-consent-loaded', '1');

    scriptNode.parentNode.insertBefore(injected, scriptNode.nextSibling);
    scriptNode.setAttribute('data-consent-loaded', '1');
    scriptNode.removeAttribute('data-src');
    remaining -= 1;
  });

  return remaining;
}

function activateDeferredEmbeds() {
  const remainingIframes = activateDeferredIframes();
  const remainingScripts = activateDeferredScripts();
  return remainingIframes + remainingScripts;
}

function bindConsentEvents() {
  let intervalId = null;

  const runActivation = () => {
    const remaining = activateDeferredEmbeds();
    if (remaining === 0 && intervalId) {
      window.clearInterval(intervalId);
      intervalId = null;
    }
  };

  EVENT_NAMES.forEach((eventName) => {
    window.addEventListener(eventName, runActivation);
    document.addEventListener(eventName, runActivation);
  });

  const observer = new MutationObserver(() => {
    runActivation();
  });

  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
    childList: true,
    subtree: true,
  });

  // Fallback for integrations that do not emit public events.
  intervalId = window.setInterval(runActivation, 1000);
  runActivation();
}

function initConsentIframes() {
  activateDeferredEmbeds();
  bindConsentEvents();

  document.addEventListener('DOMContentLoaded', activateDeferredEmbeds);
  window.addEventListener('load', activateDeferredEmbeds);
}

initConsentIframes();
